import eventbus from '@app/eventbus'
import {settings as api} from '@app/api'
import {ref} from 'vue'

////////////////////////////////////////////////////////////////////////////////////////////////////
//
// The base class for global objects. 
// A global object is data which is must be available throughout the application. 
// For instance, settings like sales invoice preferences or application settings are global objects.
// Company data is another example of a global object.
// 
// The data for global objects is typically loaded from the server at application start. 
// The data is stored in a singleton instance of the appropriate class. 
// The data is read-only. To edit the data, use the clone() method to create an editable instance.
// After editing, call save() on the editable instance. The saved data is automatically propagated to 
// the global instance. 
// 
////////////////////////////////////////////////////////////////////////////////////////////////////
class clsBaseGlobal {

    // The fillable fields. Must be overridden in implementation class. 
    // Used when loading, saving and cloning data. E.g. ["cpy_name", "cpy_code"]
    get fillable() { throw new Error('fillable must be overridden'); };


    isDataLoading = false;
    get isLoading() { return this.isDataLoading;}

    isFilling = false;
    isEditable = false;

    initialState = null;    // initial state is used to check if it is dirty


    // The typename is mandatory. E.g. "company"
    typeName = null;

    // Helper for making a field required
    rules = {
        mandatory: [ (v) => !!this.isDataLoading || !!v || "Veld is verplicht" ]
    }

    /**
     * Use the clone method to create an editable instance of this object. 
     * To edit the data is the ONLY reason to ever use the clone method, and not just reference the 
     * global data directly. 
     * 
     * When cloning, a new object is created and fresh data is loaded from the server. 
     * 
     * In summary, the whole sequence for editing settings is: 
     * import globals from '@app/globals'
     * const editableCompany = globals.company.clone();
     * ... and that's it.
     * 
     * After editableCompany.save(), the saved data  is propagated to the manager and made available to the app via 
     * the global instance.
     * 
     * Note that the cloned instance is in a loading state.
     * 
     * @returns 
     */
    clone() {
        let obj = ref(new this.constructor(this.typeName, true /* editable */)).value;
        obj.load();
        return obj; 
    }

    /**
     * Fill the data. Overwrite when appropriate.
     * 
     * @param {} data 
     */
    fill(data) {
        this.isFilling = true;
        data = data || {}

        (this.fillable||[]).forEach( (field) => {
            if (undefined == data[field]) {
                data[field] = null;
            }
        })
        for (var key in data) {
            if (undefined !== this[key]) {
                this[key] = data[key];
            }
        }
        this.afterFill(data);
        this.initialState = this._getState();        
        this.isFilling = false;
    }

    // Convert the fillables to JSON
    // arr is optional.
    toJSON() {
        let result = {};
        var self = this;
        (this.fillable || []).forEach(  ( item ) => result[item] = self[item] ) 
       
        return result;
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // State / dirty checking
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////

    /**
     * Get the current state, which is basicly just a stringification fo the json representation
     */    
    _getState() {
        var json = this.toJSON();
        return JSON.stringify(json).replaceAll('false', '0').replaceAll('true', '1');
    }

    /**
     * Is any data modified compared to the state after loading?
     * @returns 
     */
    get isModified() {
        if (this.isFilling) {
            return false; // when filling, we are not modified.
        }
        if (this.isDataLoading) {
            return false; // when loading, we are not modified.
        }
        var state = this._getState();
        return state != this.initialState;
    }

    // Although I am very not happy with this, it is the only way to do this in vue 2.7
    // We cannot use a mixin, because mixins are not supported in script setup.
    // We cannot use a composable, because we need access to the model instance (this).
    // 
    // In addition, I think the general message on page leave is confusing.  
    // When clicking 'OK', the data is not saved. Any way, since it was already in, keep it for now.
    // 
    // Usage (workaround vue 2.7):  
    //      const inst = getCurrentInstance();
    //      inst.proxy.constructor.options.beforeRouteLeave = [(a,b,c) => model.onRouteLeaveDirtyCheck(a,b,c)] 
    // 
    async onRouteLeaveDirtyCheck (to, from, next) {
    
        try {
            if (!this.isModified) {
                next()
                return;
            } else {
                await eventbus.dialog.confirm.promise({title: 'U heft wijzigingen gemaakt', body: 'Wilt u doorgaan?<br>De wijzigingen worden dan niet opgeslagen.'})
                next()
            }
        } catch (e) {
            next(false)
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // Loading and saving
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////
    /*
     * Load data from the server. 
     * As a side effect, we send a loaded event so that the manager can 
     * fill the fresh new data in the appropriate global instance.
     */
    async load() {
        this.isDataLoading = true;
        this.isDirty = false;

        try {
            let result = await api.load(this.typeName);
            // call the loaded event so that the manager can als refresh the global data.
            eventbus.appdata.loaded({settings: result.data}); 
            this.fill(result.data[this.typeName]);
        }
        finally {
            this.isDataLoading = false;
        }
    }
    /*
     * save settings data to the server. As a side effect, we refresh our own settings. 
     */
    async save(data) {
        if (!this.isEditable) {
            throw new Error('Current settings object is not editable');
        }
        this.isDataLoading = true;
        var json = this.toJSON();
        try {
            let result = await api.save(this.typeName, json);
            // Propagate the resupt so that the global data in the manager is updated as well.
            eventbus.appdata.loaded({settings: result.data}); // Will call the fill method via register events.
            this.fill(result.data[this.typeName]);
        }
        finally {
            this.isDataLoading = false;
        }
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////
    //
    // Overridables
    //
    ////////////////////////////////////////////////////////////////////////////////////////////////////
    afterFill(data) {
        // Overwrite when needed
    }

    // Constructor
    constructor(typeName, isEditable) {
        this.fillable;  // by calling it here, we enforce that the derived class implements it. 

        if (!typeName) {
            throw new Error('typeName is required');
        }
        this.typeName = typeName;
        this.isEditable = !!isEditable;
//        console.log('Created', typeName, isEditable?'Editable':'Read-Only', this);
    }
}

export default clsBaseGlobal;
